/**
 * LanguageSwitcher — the public site's locale control.
 *
 * Offers one option per supported locale (from site settings) and persists the
 * visitor's choice as a preference. Choosing a locale does not swap content
 * that hasn't been composed yet: until a page exists in the chosen locale it
 * renders in the default locale, and the active-locale marker says so honestly.
 */
import { useEffect, useRef, useState } from "react";
import { DEFAULT_LOCALE } from "@diality/contracts";
import {
  readLocalePreference,
  useSupportedLocales,
  writeLocalePreference,
} from "./locales";

export function LanguageSwitcher() {
  const supported = useSupportedLocales();
  const [preference, setPreference] = useState<string>(DEFAULT_LOCALE);
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const stored = readLocalePreference();
    if (stored) setPreference(stored);
  }, []);

  // Pages render in the default locale until composed in the visitor's choice
  // (per-locale composition arrives with page localization), so the rendered
  // locale is the default — not the preference.
  const renderedLocale = DEFAULT_LOCALE;

  const choose = (locale: string) => {
    writeLocalePreference(locale);
    setPreference(locale);
  };

  return (
    <div className="flex items-center gap-3 text-sm">
      <button
        type="button"
        data-language-switcher
        aria-label="Choose language"
        onClick={() => firstOptionRef.current?.focus()}
        className="font-medium text-muted-foreground"
      >
        Language
      </button>
      <ul className="flex items-center gap-1">
        {supported.map((locale, index) => (
          <li key={locale}>
            <button
              ref={index === 0 ? firstOptionRef : undefined}
              type="button"
              data-language-option
              data-locale={locale}
              aria-pressed={preference === locale}
              onClick={() => choose(locale)}
              className={
                preference === locale
                  ? "rounded px-2 py-1 font-semibold text-primary"
                  : "rounded px-2 py-1 text-muted-foreground hover:text-foreground"
              }
            >
              {locale}
            </button>
          </li>
        ))}
      </ul>
      <span
        data-active-locale
        data-locale={renderedLocale}
        className="text-muted-foreground"
      >
        English
      </span>
    </div>
  );
}
